// ============================================================================
// Hooks TanStack Query — PROFIL (L16) : GET /me + GET /:email + auto-edition
// ============================================================================
// Hook = TanStack Query SEULEMENT (regle parc). L'autorisation vit au SERVEUR :
// le SPA consomme `tabs`/`can`/`self` renvoyes par l'API — il ne RECALCULE JAMAIS
// la matrice au callsite (lecon GA-05). Un onglet absent de `tabs` n'est pas
// seulement masque : sa donnee n'a jamais ete lue cote serveur.

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiForbiddenError, ApiServerError } from '@gs/api-client';

export type GestProfileRole = 'admin' | 'manager' | 'director' | 'employee';
export type ProfileTab = 'apercu' | 'pouls' | 'objectifs' | 'documents' | 'remuneration' | 'gestion';

export interface NovaScores {
  red: number | null;
  yellow: number | null;
  green: number | null;
  blue: number | null;
}

export interface ProfileDto {
  email: string;
  fullName: string | null;
  /** F-814 : deja null cote serveur si le viewer n'a pas canManageEmployee. */
  matricule: string | null;
  teamId: string | null;
  teamName: string | null;
  jobTitle: string | null;
  tasksDescription: string | null;
  /** NT-L16-03 : deja vide cote serveur si le viewer n'a pas la garde management. */
  gallupStrengths: string[];
  nova: NovaScores;
  committee: string | null;
  responsable: { email: string; name: string | null } | null;
  systemCRole: string | null;
  /** D3 / STOP-8 : dirige au moins une equipe (Teams.director_email). */
  isTeamDirector?: boolean;
}

export interface MemberPulse {
  period: string;
  workloadScore: number;
  energyScore: number;
}

export interface EmployeeObjective {
  id: string;
  title: string;
  status: string;
  targetDate: string | null;
}

export interface GridEchelon { echelon: number; rate: number }
export interface GridNiveau { level: number; label: string; echelonMax: number | null }
export interface GridClass {
  label: string;
  augGrillePct: number | null;
  baseHoursPerWeek: number;
  weeksPerYear: number;
  echelons: GridEchelon[];
  niveaux: GridNiveau[];
}

/** Point frise / historique — DTO profil (pas de note). */
export interface ProfileSalaryHistoryPoint {
  year: number;
  annual: number;
  role: string | null;
}

export interface ProfileRateChangePoint {
  hourlyRate: number;
  effectiveDate: string;
}

export interface ProfileBonusSeriesPoint {
  year: number;
  actualAmount: number;
  targetAmount: number;
}

/** Point de frise salariale (miroir SalaryTimeline serveur, lecture seule). */
export interface ProfileSalaryPoint {
  year: number;
  echelon: number;
  hourly: number | null;
  annual: number | null;
  realHourly: number | null;
  realAnnual: number | null;
  roleLabel: string | null;
  kind: 'history' | 'current' | 'projection';
  isChange: boolean;
  isRoleChange: boolean;
  isHire: boolean;
}

export interface ProfileSalaryTimeline {
  points: ProfileSalaryPoint[];
  currentAnnual: number | null;
  currentHourly: number | null;
  currentEchelon: number;
  maxEchelon: number;
}

/**
 * Vue compensation (F-845..F-849, FRAG 7). M-816 : `offGridReasonPublic` est la
 * SEULE raison servie. Boni = montants (pas de note) ; avantages = agregat annuel.
 */
export interface ProfileCompensation {
  classId: string | null;
  currentEchelon: number;
  currentNiveau: number | null;
  hireDate: string | null;
  currentHourlyRate: number | null;
  departureDate: string | null;
  offGrid: boolean;
  offGridReasonPublic: string | null;
  gridYear: number;
  weeklyHours: number;
  /** null = F-846 (classe absente de la grille de l'annee). */
  gridClass: GridClass | null;
  salaryAnnual: number | null;
  bonusActualAmount: number;
  bonusTargetAmount: number;
  bonusTargetPct: number | null;
  benefitsAnnual: number;
  totalAnnual: number | null;
  salaryHistory: ProfileSalaryHistoryPoint[];
  rateChanges: ProfileRateChangePoint[];
  timeline: ProfileSalaryTimeline | null;
  bonusSeries: ProfileBonusSeriesPoint[];
}

export interface ProfileOverview {
  pulses: MemberPulse[];
  objectives: EmployeeObjective[];
}

export interface ProfileCan {
  pulse: boolean;
  documents: boolean;
  compensation: boolean;
  manage: boolean;
  /** NT-L16-03 : garde management sur les forces/Nova d'un tiers. */
  strengths: boolean;
}

export interface ProfileResponse {
  /** M-800 : true -> le SPA redirige vers /profile (self-redirect). */
  self: boolean;
  /** null = aucune fiche gest.EmployeeProfiles (donnees non migrees) -> EmptyState. */
  profile: ProfileDto | null;
  tabs: ProfileTab[];
  can?: ProfileCan;
  overview: ProfileOverview | null;
  compensation: ProfileCompensation | null;
  viewer: { role: GestProfileRole; email: string };
}

export interface MetaPayload {
  jobTitle: string | null;
  tasksDescription: string | null;
}

export interface StrengthsPayload {
  gallupStrengths: string[];
  nova: NovaScores;
}

const ME_KEY = ['gest', 'profile', 'me'] as const;
const memberKey = (email: string) => ['gest', 'profile', 'member', email] as const;

/** Code court d'erreur pour `?error=` / alerte (jamais de code brut a l'ecran, GA-21). */
export function profileErrorCode(err: unknown): string {
  if (err instanceof ApiForbiddenError) return 'forbidden';
  if (err instanceof ApiServerError) {
    if (err.status === 404) return 'notfound';
    if (err.status === 400) return 'invalid';
  }
  return 'server';
}

/** F-800 : SA fiche — onglets fixes, aucune garde de role au-dela du standing GEST. */
export function useMyProfile(enabled: boolean) {
  return useQuery({
    queryKey: ME_KEY,
    queryFn: () => api.get<ProfileResponse>('/api/profile/me'),
    enabled,
    retry: false,
  });
}

/** F-810 : fiche d'un membre — `self:true` declenche le self-redirect M-800. */
export function useMemberProfile(email: string | undefined, enabled: boolean) {
  return useQuery({
    queryKey: memberKey(email ?? ''),
    queryFn: () => api.get<ProfileResponse>(`/api/profile/${encodeURIComponent(email ?? '')}`),
    enabled: enabled && !!email,
    // 404 (F-811) et 403 = etats stables — pas de retry.
    retry: false,
  });
}

function useInvalidateMyProfile() {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: ME_KEY });
    // M-811 (revalidation ciblee) : l'organigramme affiche job_title/description —
    // il doit refleter l'edition. Pendant de `revalidatePath('/organigramme')`.
    void qc.invalidateQueries({ queryKey: ['gest', 'org'] });
  };
}

/** M-807 / F-837 : auto-edition titre + description — AUCUN ciblage tiers possible. */
export function useUpdateMyMeta() {
  const invalidate = useInvalidateMyProfile();
  return useMutation({
    mutationFn: (payload: MetaPayload) => api.put<{ ok: boolean }>('/api/profile/me/meta', payload),
    onSuccess: () => invalidate(),
  });
}

/** M-807 / F-839..F-844 : auto-edition forces Gallup + couleurs Nova. */
export function useUpdateMyStrengths() {
  const invalidate = useInvalidateMyProfile();
  return useMutation({
    mutationFn: (payload: StrengthsPayload) => api.put<{ ok: boolean }>('/api/profile/me/strengths', payload),
    onSuccess: () => invalidate(),
  });
}
